import { useState, useCallback, useEffect, useMemo } from "react";
import { Link } from "wouter";
import { ToolContainer, FileDropZone, ProcessingState, DownloadButton, ResultPreview } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RotateCcw as RotateIcon, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { ReorderableFileList, findDuplicateFiles } from "@/components/ReorderableFileList";
import { trackToolCompleted } from "@/lib/analytics";
import { formatFileSize } from "@/lib/formatFileSize";
import { plural } from "@/lib/plural";
import { useRunToken } from "@/hooks/useRunToken";
import { TrialFooterNotice } from "@/components/TrialFooterNotice";
import { rotateImageToFile, convertHeicToJpegFile } from "./operations";
import {
  buildImagePdf,
  normalizeImageFile,
  parseCustomPageSize,
  pdfNameForImages,
  ImagePdfCancelled,
  ImageReadError,
  DEFAULT_IMAGE_PDF_OPTIONS,
  type ImagePdfOptions,
  type ImagePdfResult,
  type MarginChoice,
  type PageSpec,
} from "./imagePdf";
import { loadPdfDocument, renderPageToJpegBlob } from "./utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Combines one or more images into a single PDF, one image per page (or two per page), fitted to A4, US Letter, a custom size, or the image's own size. Accepts JPG, PNG, WebP, GIF (first frame), BMP, SVG and HEIC (the format an iPhone saves by default). Phone photos are turned upright using the orientation the camera stored, so a portrait photo comes out portrait. JPG and PNG files are embedded as they are, without re-compressing them.`,
  how: `Add your images; each shows a thumbnail so it is easy to tell them apart. Drag to reorder, rotate any that are sideways, then choose the page size, orientation, margin, and whether each image fits whole inside the page or fills it. Convert, check the strip of pages, and download. Nothing is uploaded; it all runs in your browser.`,
  tip: `Photograph documents against a plain, well-lit background; the tool does not straighten or de-shadow a photo. “Fill the page” crops the edges of an image to cover the page, which suits photo prints but not documents. TIFF files are not supported yet (convert them to JPG or PNG first), and an animated GIF contributes only its first frame.`,
};

type RotationDegrees = 0 | 90 | 180 | 270;
type Orientation = "portrait" | "landscape";
type Unit = "in" | "mm" | "cm";
type SizeChoice = "a4" | "letter" | "original" | "custom";
const ACCEPTED =
  "image/png,image/jpeg,image/jpg,image/webp,image/heic,image/heif,image/gif,image/bmp,image/svg+xml,image/avif,.jfif,.heic,.heif,.webp,.gif,.bmp,.svg,.avif";
const isImageFile = (f: File) => f.type.startsWith("image/") || /\.(jpe?g|jfif|png|webp|gif|bmp|svg|avif|heic|heif|tiff?)$/i.test(f.name);

function OptionRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-muted-foreground w-24 shrink-0">{label}</span>
      {children}
    </div>
  );
}

function Choice<T>({ value, current, onPick, children }: { value: T; current: T; onPick: (v: T) => void; children: React.ReactNode }) {
  return (
    <Button type="button" size="sm" variant={current === value ? "default" : "outline"} aria-pressed={current === value} onClick={() => onPick(value)}>
      {children}
    </Button>
  );
}

export default function ImageToPdf() {
  const [files, setFiles] = useState<File[]>([]);
  const [rotations, setRotations] = useState<RotationDegrees[]>([]);
  const [preparing, setPreparing] = useState(false);
  const [preparingCount, setPreparingCount] = useState(0);
  const [sizeChoice, setSizeChoice] = useState<SizeChoice>(() => (typeof navigator !== "undefined" && /^en-(US|CA)|^es-MX/i.test(navigator.language) ? "letter" : "a4"));
  const [orientation, setOrientation] = useState<Orientation>("portrait");
  const [unit, setUnit] = useState<Unit>("in");
  const [customW, setCustomW] = useState("8.5");
  const [customH, setCustomH] = useState("11");
  const [margin, setMargin] = useState<MarginChoice>("normal");
  const [fit, setFit] = useState<"fit" | "fill">("fit");
  const [originalDpi, setOriginalDpi] = useState<ImagePdfOptions["originalDpi"]>("auto");
  const [combineOnePage, setCombineOnePage] = useState(false);
  const [splitDirection, setSplitDirection] = useState<"vertical" | "horizontal">("vertical");
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<{ blob: Blob; r: ImagePdfResult; name: string } | null>(null);
  const [pageThumbs, setPageThumbs] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [problems, setProblems] = useState<string[]>([]);
  const [notes, setNotes] = useState<string[]>([]);
  const runs = useRunToken();
  useEffect(() => () => runs.cancel(), [runs]);

  // Object URLs for the list thumbnails, revoked when the list changes.
  const thumbnailUrls = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => thumbnailUrls.forEach((u) => URL.revokeObjectURL(u)), [thumbnailUrls]);

  // Anything that changes the PDF clears the old result, so Download never serves a stale file.
  const clearResult = () => {
    runs.cancel();
    setProcessing(false);
    setResult(null);
    setPageThumbs([]);
    setErrorMessage("");
  };

  const handleFiles = useCallback(
    async (newFiles: File[]) => {
      const problemsNow: string[] = [];
      const notesNow: string[] = [];
      const candidates: File[] = [];
      for (const f of newFiles) {
        if (isImageFile(f)) candidates.push(f);
        else problemsNow.push(`${f.name} is not an image and was skipped${/\.pdf$/i.test(f.name) ? ". To work with PDFs use PDF to JPG or Merge PDF" : ""}.`);
      }
      setPreparing(true);
      setPreparingCount(candidates.length);
      const ready: File[] = [];
      for (const f of candidates) {
        try {
          if (/\.gif$/i.test(f.name) || f.type === "image/gif") {
            const { isAnimatedGif } = await import("./imagePdf");
            if (isAnimatedGif(new Uint8Array(await f.arrayBuffer()))) notesNow.push(`${f.name} is an animated GIF. A PDF page cannot move, so only its first frame is used.`);
          }
          // HEIC → JPEG first; then every file is made upright and embeddable (GIF/BMP/SVG/WebP → PNG).
          const norm = await normalizeImageFile(await convertHeicToJpegFile(f));
          // The list keeps the name that was dropped (animation.gif, not animation.png).
          ready.push(norm.name === f.name ? norm : new File([norm], f.name, { type: norm.type }));
        } catch (err) {
          const why = err instanceof ImageReadError ? err.message : "could not be read";
          problemsNow.push(`${f.name}: ${why.charAt(0).toUpperCase()}${why.slice(1)}. It was not added.`);
        }
      }
      setPreparing(false);
      if (ready.length) {
        setFiles((prev) => {
          const dupes = findDuplicateFiles(prev, ready);
          if (dupes.length > 0) toast.warning(`${dupes.length === 1 ? "This image is" : `${dupes.length} of these images are`} already in your list, added anyway.`);
          return [...prev, ...ready];
        });
        setRotations((prev) => [...prev, ...ready.map(() => 0 as RotationDegrees)]);
      }
      setProblems(problemsNow);
      setNotes((prev) => [...prev, ...notesNow]);
      clearResult();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const removeFile = (index: number) => {
    if (files.length <= 1) setNotes([]);
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setRotations((prev) => prev.filter((_, i) => i !== index));
    clearResult();
  };

  const reorderFiles = (newFiles: File[]) => {
    // Rotation is tracked by index, so it has to travel with its file.
    const newRotations = newFiles.map((f) => rotations[files.indexOf(f)] ?? 0);
    setFiles(newFiles);
    setRotations(newRotations);
    clearResult();
  };

  const rotateFile = (index: number) => {
    setRotations((prev) => prev.map((r, i) => (i === index ? (((r + 90) % 360) as RotationDegrees) : r)));
    clearResult();
  };

  const custom = sizeChoice === "custom" ? parseCustomPageSize(customW, customH, unit) : null;
  const customError = custom && !custom.ok ? custom.message : "";

  const resolvePageSpec = (): PageSpec | null => {
    if (sizeChoice === "original") return "original";
    if (sizeChoice === "custom") {
      if (!custom || !custom.ok) return null;
      const [a, b] = [custom.widthPt, custom.heightPt];
      return orientation === "landscape" ? { widthPt: Math.max(a, b), heightPt: Math.min(a, b) } : { widthPt: Math.min(a, b), heightPt: Math.max(a, b) };
    }
    const preset = sizeChoice === "a4" ? { w: 595.28, h: 841.89 } : { w: 612, h: 792 };
    return orientation === "landscape" ? { widthPt: preset.h, heightPt: preset.w } : { widthPt: preset.w, heightPt: preset.h };
  };

  const convert = async () => {
    if (files.length === 0) return;
    const pageSize = resolvePageSpec();
    if (!pageSize) return;
    const run = runs.start();
    setProcessing(true);
    setProgress(0);
    setErrorMessage("");
    setResult(null);
    setPageThumbs([]);
    try {
      const rotated = await Promise.all(files.map((f, i) => rotateImageToFile(f, rotations[i] ?? 0)));
      if (!runs.isCurrent(run)) return;
      const r = await buildImagePdf(
        rotated,
        { ...DEFAULT_IMAGE_PDF_OPTIONS, pageSize, margin, fit, originalDpi, pair: combineOnePage && files.length > 1 ? splitDirection : null },
        { isCurrent: () => runs.isCurrent(run), progress: (c, t) => runs.isCurrent(run) && setProgress((c / t) * 100) },
      );
      if (!runs.isCurrent(run)) return;
      const blob = new Blob([r.bytes as BlobPart], { type: "application/pdf" });
      const usedNames = files.filter((f) => !r.skipped.some((s) => s.name === f.name)).map((f) => f.name);
      setResult({ blob, r, name: pdfNameForImages(usedNames.length ? usedNames : files.map((f) => f.name)) });
      toast.success(`Converted ${plural(r.images, "image")} to PDF`);
      trackToolCompleted("image-to-pdf", "pdf-tools");
      // A strip of every page, so a wrong orientation or crop is seen before downloading.
      void (async () => {
        try {
          const doc = await loadPdfDocument(new File([blob], "x.pdf", { type: "application/pdf" }));
          const out: string[] = [];
          for (let i = 1; i <= Math.min(doc.numPages, 40); i++) {
            const page = await doc.getPage(i);
            const vp = page.getViewport({ scale: 1 });
            const jpg = await renderPageToJpegBlob(page, 120 / Math.max(vp.width, vp.height), 0.7);
            if (!runs.isCurrent(run)) return;
            out.push(URL.createObjectURL(jpg));
          }
          if (runs.isCurrent(run)) setPageThumbs(out);
        } catch {
          /* the strip is a courtesy */
        }
      })();
    } catch (err) {
      if (err instanceof ImagePdfCancelled || !runs.isCurrent(run)) return;
      console.error("Image to PDF conversion failed:", err);
      setErrorMessage(err instanceof ImageReadError ? err.message : "The conversion failed. Try fewer or smaller images.");
    } finally {
      if (runs.isCurrent(run)) setProcessing(false);
    }
  };

  useEffect(() => () => pageThumbs.forEach((u) => URL.revokeObjectURL(u)), [pageThumbs]);

  const reset = () => {
    runs.cancel();
    setFiles([]); setNotes([]);
    setRotations([]);
    setResult(null);
    setPageThumbs([]);
    setProblems([]);
    setErrorMessage("");
    setProcessing(false);
  };

  const withClear = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); clearResult(); };
  const pickDpi = withClear<ImagePdfOptions["originalDpi"]>(setOriginalDpi);

  return (
    <ToolContainer>
      <FileDropZone gateAtDownload accept={ACCEPTED} acceptLabel="JPG, PNG, WebP, GIF, BMP, SVG, AVIF or HEIC image" multiple onFiles={handleFiles} label="Drop images here or take photos" description="JPG, PNG, WebP, GIF, BMP, SVG or HEIC images (or camera photos), combined into one PDF" />
      {preparing && <p className="mt-3 text-sm text-muted-foreground" role="status">Preparing {preparingCount > 1 ? `${preparingCount} images` : "images"}…</p>}
      {notes.length > 0 && (
        <NoticeBox role="status" className="mt-4 text-sm space-y-1">
          <div className="flex gap-2 font-medium"><Info className="h-4 w-4 mt-0.5 shrink-0 text-primary" />Good to know</div>
          {notes.map((n, i) => <p key={i}>{n}</p>)}
        </NoticeBox>
      )}
      {problems.length > 0 && (
        <NoticeBox role="alert" className="mt-4 text-sm space-y-1">
          <div className="flex gap-2 font-medium"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-primary" />Some files were not added</div>
          {problems.map((p, i) => <p key={i}>{p}</p>)}
          {problems.some((p) => /\.pdf/i.test(p)) && (
            <p className="text-muted-foreground">
              Try <Link href="/tool/pdf-to-jpg" className="underline">PDF to JPG</Link> or <Link href="/tool/merge-pdf" className="underline">Merge PDF</Link>.
            </p>
          )}
        </NoticeBox>
      )}
      {files.length > 0 && (
        <div className="mt-6 space-y-4">
          <fieldset className="space-y-3" disabled={processing}>
            <legend className="text-sm font-medium mb-1">Page setup</legend>
            <div className="flex flex-wrap items-center gap-2">
              <Label className="text-sm text-muted-foreground w-24 shrink-0">Page size</Label>
              <div className="w-56">
                <Select value={sizeChoice} onValueChange={(v) => { setSizeChoice(v as SizeChoice); clearResult(); }}>
                  <SelectTrigger aria-label="Page size"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="a4">A4</SelectItem>
                    <SelectItem value="letter">US Letter</SelectItem>
                    <SelectItem value="original">Original image size</SelectItem>
                    <SelectItem value="custom">Custom size…</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {sizeChoice === "custom" && (
              <div className="space-y-1">
                <div className="flex flex-wrap items-end gap-3 pl-0 sm:pl-[6.5rem]">
                  <div>
                    <Label htmlFor="img-w" className="mb-1 block text-xs text-muted-foreground">Width</Label>
                    <Input id="img-w" inputMode="decimal" value={customW} onChange={(e) => { setCustomW(e.target.value); clearResult(); }} className="w-24" aria-invalid={!!customError} />
                  </div>
                  <span className="text-muted-foreground pb-2">×</span>
                  <div>
                    <Label htmlFor="img-h" className="mb-1 block text-xs text-muted-foreground">Height</Label>
                    <Input id="img-h" inputMode="decimal" value={customH} onChange={(e) => { setCustomH(e.target.value); clearResult(); }} className="w-24" aria-invalid={!!customError} />
                  </div>
                  <div role="group" aria-label="Unit" className="flex gap-1 pb-0.5">
                    {(["in", "mm", "cm"] as Unit[]).map((u) => (
                      <Button key={u} type="button" size="sm" variant={unit === u ? "default" : "outline"} aria-pressed={unit === u} onClick={() => {
                        if (u !== unit) {
                          const f = { in: 1, mm: 25.4, cm: 2.54 };
                          const conv = (v: string) => { const n = Number(v.replace(",", ".")); return Number.isFinite(n) && v.trim() ? String(Math.round((n / f[unit]) * f[u] * 100) / 100) : v; };
                          setCustomW(conv(customW));
                          setCustomH(conv(customH));
                          setUnit(u);
                          clearResult();
                        }
                      }}>{u}</Button>
                    ))}
                  </div>
                </div>
                {customError && <p role="alert" className="text-sm text-destructive pl-0 sm:pl-[6.5rem]">{customError}</p>}
              </div>
            )}

            {sizeChoice !== "original" && (
              <>
                <OptionRow label="Orientation">
                  <Choice value="portrait" current={orientation} onPick={withClear(setOrientation)}>Portrait</Choice>
                  <Choice value="landscape" current={orientation} onPick={withClear(setOrientation)}>Landscape</Choice>
                </OptionRow>
                <OptionRow label="Margin">
                  <Choice value="none" current={margin} onPick={withClear(setMargin)}>None</Choice>
                  <Choice value="small" current={margin} onPick={withClear(setMargin)}>Small</Choice>
                  <Choice value="normal" current={margin} onPick={withClear(setMargin)}>Normal</Choice>
                </OptionRow>
                <OptionRow label="Image on page">
                  <Choice value="fit" current={fit} onPick={withClear(setFit)}>Fit whole image</Choice>
                  <Choice value="fill" current={fit} onPick={withClear(setFit)}>Fill the page (crops edges)</Choice>
                </OptionRow>
              </>
            )}
            {sizeChoice === "original" && (
              <OptionRow label="Resolution">
                <Choice value="auto" current={originalDpi} onPick={pickDpi}>Image's own</Choice>
                {([72, 96, 150, 300] as const).map((d) => <Choice key={d} value={d} current={originalDpi} onPick={pickDpi}>{d} dpi</Choice>)}
              </OptionRow>
            )}

            {files.length > 1 && sizeChoice !== "original" && (
              <label className="flex items-start gap-2 border rounded-lg p-3 max-w-md cursor-pointer hover:bg-muted/40">
                <Checkbox checked={combineOnePage} onCheckedChange={(v) => { setCombineOnePage(v === true); clearResult(); }} className="mt-1" aria-label="Pair images two per page" />
                <div>
                  <div className="text-sm font-medium">Pair images two per page</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Images are paired in order (1st and 2nd share a page, and so on). An odd one out at the end gets its own page.</div>
                </div>
              </label>
            )}
            {combineOnePage && files.length > 1 && sizeChoice !== "original" && (
              <OptionRow label="Arrangement">
                <Choice value="vertical" current={splitDirection} onPick={withClear(setSplitDirection)}>Side by side</Choice>
                <Choice value="horizontal" current={splitDirection} onPick={withClear(setSplitDirection)}>Stacked</Choice>
              </OptionRow>
            )}
          </fieldset>

          <div>
            <p className="text-sm font-medium text-muted-foreground mb-2">
              {plural(files.length, "image")}: drag to reorder, use the rotate button on any that are sideways
            </p>
            <ReorderableFileList
              files={files}
              onReorder={reorderFiles}
              onRemove={removeFile}
              thumbnailUrls={thumbnailUrls}
              thumbnailFit="large"
              thumbnailRotations={rotations}
              rowActions={(i) => (
                <Button type="button" variant="ghost" size="icon" className="h-11 w-11 sm:h-7 sm:w-7" onClick={() => rotateFile(i)} title={`Rotate 90° (currently ${rotations[i] ?? 0}°)`} aria-label={`Rotate ${files[i]?.name ?? "image"} 90 degrees (currently ${rotations[i] ?? 0}°)`}>
                  <RotateIcon className="h-3.5 w-3.5" />
                </Button>
              )}
            />
          </div>

          {errorMessage && <p role="alert" className="text-sm text-destructive">{errorMessage}</p>}

          {!result && !processing && <TrialFooterNotice toolId="image-to-pdf" />}

          <div className="flex flex-wrap gap-3 pt-4 border-t">
            {processing ? (
              <ProcessingState progress={progress} onStop={() => { runs.cancel(); setProcessing(false); }} />
            ) : result ? (
              <DownloadButton blob={result.blob} fileName={result.name} />
            ) : (
              <Button onClick={convert} disabled={preparing || (sizeChoice === "custom" && !!customError)}>Convert to PDF</Button>
            )}
            <Button variant="outline" onClick={reset}>Clear all</Button>
          </div>

          {result && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground" role="status">
                {plural(result.r.pages, "page")} · {formatFileSize(result.blob.size)} · {result.name}
              </p>
              {result.r.skipped.length > 0 && (
                <NoticeBox role="alert" className="text-sm">
                  {result.r.images} of {files.length} converted. Could not read: {result.r.skipped.map((s) => `${s.name} (${s.reason})`).join(", ")}.
                </NoticeBox>
              )}
              {pageThumbs.length > 0 && (
                <div className="flex gap-2 overflow-x-auto pb-2" aria-label="Pages of the PDF">
                  {pageThumbs.map((u, i) => (
                    <figure key={i} className="shrink-0">
                      <img src={u} alt={`Page ${i + 1}`} className="max-h-40 rounded border bg-white" />
                      <figcaption className="text-xs text-muted-foreground text-center">Page {i + 1}</figcaption>
                    </figure>
                  ))}
                </div>
              )}
              <ResultPreview blob={result.blob} mimeType="application/pdf" />
            </div>
          )}
        </div>
      )}
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
